import { nowIsoUtc } from "@nijiurachan/js/util/time";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  completeR18BlurOnboarding,
  shouldShowR18BlurOnboarding,
  subscribeR18BlurOnboardingReset,
} from "@/features/r18-blur-onboarding/lib/r18BlurOnboarding";
import {
  createR18GateMediator,
  createR18ModalMediator,
  isR18OnboardingEvent,
  type R18ModalEffect,
} from "@/features/r18-blur-onboarding/onboardingMediator";
import { useSettingsStore } from "@/features/settings/hooks";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export function useR18GatePresenter(): {
  node: EventNode<TypedEvent>;
  shouldMount: boolean;
  isOpen: boolean;
} {
  const [mediator] = useState(() =>
    createR18GateMediator(shouldShowR18BlurOnboarding(), () =>
      completeR18BlurOnboarding(nowIsoUtc()),
    ),
  );
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) =>
    isR18OnboardingEvent(event) && mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled",
  );
  useEffect(
    () =>
      subscribeR18BlurOnboardingReset(() => {
        node.dispatch({ type: "r18-onboarding.reset" });
      }),
    [node],
  );
  return { ...state, node };
}

export function useR18ModalPresenter(
  isOpen: boolean,
  onDismiss: () => void,
  onComplete: () => void,
): { node: EventNode<TypedEvent>; showR18: boolean } {
  const showR18 = useSettingsStore((state) => state.showR18);
  const setShowR18 = useSettingsStore((state) => state.setShowR18);
  const applyInitialR18Cushion = useSettingsStore(
    (state) => state.applyInitialR18Cushion,
  );
  const confirmR18Preference = useSettingsStore(
    (state) => state.confirmR18Preference,
  );
  const effects = {
    setShowR18,
    applyInitialR18Cushion,
    confirmR18Preference,
    onDismiss,
    onComplete,
  };
  const latestEffects = useRef(effects);
  useLayoutEffect(() => {
    latestEffects.current = effects;
  });
  const [mediator] = useState(() =>
    createR18ModalMediator(isOpen, (effect: R18ModalEffect): void => {
      const current = latestEffects.current;
      switch (effect.type) {
        case "initialize":
          current.applyInitialR18Cushion();
          break;
        case "set-preference":
          current.setShowR18(effect.showR18);
          break;
        case "confirm":
          current.confirmR18Preference();
          current.onComplete();
          break;
        case "dismiss":
          current.onDismiss();
          break;
      }
    }),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) =>
    isR18OnboardingEvent(event) && mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled",
  );
  useLayoutEffect(() => {
    const event = { type: "r18-onboarding.modal-sync", isOpen } as const;
    node.dispatch(event);
  }, [node, isOpen]);
  return { showR18, node };
}
